// Клиентский реестр модулей — единственная точка веба, знающая ВСЕ веб-вклады.
//
// Импорты СТАТИЧЕСКИЕ и останутся такими. Динамический `import.meta.glob` дал
// бы «реестр без списка»: манифест-сирота не ловится ревью, tree-shaking
// перестаёт работать (в бандл едут все манифесты, включая выключенные модули),
// а вывод типов схлопывается до `WebModuleManifest[]` без литералов. Цена —
// одна строка на модуль; она же служит гейтом «манифест написан, но нигде не
// подключён» (класс wire-up-дыр, CLAUDE.md §14).
//
// ⚠️ Здесь НЕТ `ModuleManifest` из `@studyhub/shared/module`: серверные
// манифесты в веб-бандл не попадают (см. шапку `packages/shared/src/module/
// index.ts`). Способности модуля (kind / lifecycle / minTier / learning.topic)
// придут клиенту с сервера на Ш3; сейчас веб знает только СВОЙ вклад.

import type { ModuleId } from "@studyhub/shared/module";
import type { Category } from "@studyhub/ui/components/studyhub/category";
import type {
	CourseToolStep,
	NavBadgeSource,
	ToolChannel,
	WebLaunchTile,
	WebModuleManifest,
	WebNavEntry,
	WebSectionIntro,
	WebWorkspaceTool,
} from "@/modules/_contract";
import { DEFAULT_SURFACE_ORDER } from "@/modules/_contract";
import { audioRecapWebManifest } from "@/modules/audio-recap.manifest";
import { calendarWebManifest } from "@/modules/calendar.manifest";
import { charactersWebManifest } from "@/modules/characters.manifest";
import { courseTemplatesWebManifest } from "@/modules/course-templates.manifest";
import { coursesWebManifest } from "@/modules/courses.manifest";
import { essaysWebManifest } from "@/modules/essays.manifest";
import { flashcardsWebManifest } from "@/modules/flashcards.manifest";
import { glossaryWebManifest } from "@/modules/glossary.manifest";
import { libraryWebManifest } from "@/modules/library.manifest";
import { materialsWebManifest } from "@/modules/materials.manifest";
import { notesWebManifest } from "@/modules/notes.manifest";
import { progressWebManifest } from "@/modules/progress.manifest";
import { quizzesWebManifest } from "@/modules/quizzes.manifest";
import { recordingsWebManifest } from "@/modules/recordings.manifest";
import { trashWebManifest } from "@/modules/trash.manifest";
import { tutorWebManifest } from "@/modules/tutor.manifest";
import { visualExplainerWebManifest } from "@/modules/visual-explainer.manifest";
import { voiceTutorWebManifest } from "@/modules/voice-tutor.manifest";
import { weakSpotsWebManifest } from "@/modules/weak-spots.manifest";

/** Все подключённые веб-вклады. Порядок — по группам `kind`, как в `ids.ts`. */
export const WEB_MODULE_REGISTRY: readonly WebModuleManifest[] = [
	// learning-format
	notesWebManifest,
	quizzesWebManifest,
	flashcardsWebManifest,
	glossaryWebManifest,
	audioRecapWebManifest,
	voiceTutorWebManifest,
	// capture
	recordingsWebManifest,
	// standalone-tool
	essaysWebManifest,
	visualExplainerWebManifest,
	// platform-capability
	tutorWebManifest,
	// meta
	materialsWebManifest,
	libraryWebManifest,
	coursesWebManifest,
	calendarWebManifest,
	courseTemplatesWebManifest,
	// derivative
	progressWebManifest,
	weakSpotsWebManifest,
	// lifecycle
	trashWebManifest,
	// external-module — Ш7, федерация с CharacterHub
	charactersWebManifest,
];

// ─── Производные (чистые функции поверх произвольного реестра) ───────────────
//
// Аргументом, а не импортом: ровно эти функции прогоняет контрольный тест на
// ФИКТИВНОМ реестре — иначе «поверхность читает реестр» проверялась бы на том
// же реестре, что и живёт в проде, то есть не проверялась бы вовсе.

/** Пункт навигации вместе с модулем-владельцем. */
export interface OwnedNavEntry {
	/**
	 * `ModuleId`, а не `string`: владелец пункта — вход гейта доступности
	 * (`use-nav-availability`), и опечатка в нём обязана ловиться компилятором,
	 * а не молча превращаться в «модуль неизвестен, показываем как есть».
	 */
	readonly moduleId: ModuleId;
	readonly entry: WebNavEntry;
}

function orderOf(value: number | undefined): number {
	return value ?? DEFAULT_SURFACE_ORDER;
}

/** Все пункты навигации реестра в каноническом порядке (`order`, затем реестр). */
export function moduleNavEntries(
	registry: readonly WebModuleManifest[] = WEB_MODULE_REGISTRY,
): OwnedNavEntry[] {
	const flat: OwnedNavEntry[] = [];
	for (const manifest of registry) {
		for (const entry of manifest.nav ?? []) flat.push({ moduleId: manifest.id, entry });
	}
	return flat.toSorted((a, b) => orderOf(a.entry.order) - orderOf(b.entry.order));
}

/** Источники счётчиков — вход `use-nav-badges`. Ключ — путь пункта. */
export function moduleNavBadges(
	registry: readonly WebModuleManifest[] = WEB_MODULE_REGISTRY,
): { readonly to: string; readonly moduleId: ModuleId; readonly source: NavBadgeSource }[] {
	const out: { to: string; moduleId: ModuleId; source: NavBadgeSource }[] = [];
	for (const { moduleId, entry } of moduleNavEntries(registry)) {
		if (entry.badge) out.push({ to: entry.to, moduleId, source: entry.badge });
	}
	return out;
}

/** Плитка витрины вместе с модулем-владельцем. */
export interface OwnedLaunchTile {
	readonly moduleId: ModuleId;
	readonly tile: WebLaunchTile;
}

/** Плитки витрины возможностей в каноническом порядке, с владельцем. */
export function moduleLaunchTilesOwned(
	registry: readonly WebModuleManifest[] = WEB_MODULE_REGISTRY,
): OwnedLaunchTile[] {
	const out: OwnedLaunchTile[] = [];
	for (const manifest of registry) {
		for (const tile of manifest.launch ?? []) out.push({ moduleId: manifest.id, tile });
	}
	return out.toSorted((a, b) => orderOf(a.tile.order) - orderOf(b.tile.order));
}

/** Плитки витрины возможностей в каноническом порядке. */
export function moduleLaunchTiles(
	registry: readonly WebModuleManifest[] = WEB_MODULE_REGISTRY,
): WebLaunchTile[] {
	return moduleLaunchTilesOwned(registry).map((owned) => owned.tile);
}

/** Плитка «Инструменты» вместе с модулем-владельцем. */
export interface OwnedWorkspaceTool {
	readonly moduleId: ModuleId;
	readonly tool: WebWorkspaceTool;
}

/** Все плитки воркспейса реестра (без разбивки по шагам). */
export function moduleWorkspaceTools(
	registry: readonly WebModuleManifest[] = WEB_MODULE_REGISTRY,
): OwnedWorkspaceTool[] {
	const out: OwnedWorkspaceTool[] = [];
	for (const manifest of registry) {
		for (const tool of manifest.workspaceTools ?? []) out.push({ moduleId: manifest.id, tool });
	}
	return out;
}

/** Плитки одного шага темы в порядке, объявленном модулями. */
export function moduleWorkspaceToolsByStep(
	step: CourseToolStep,
	registry: readonly WebModuleManifest[] = WEB_MODULE_REGISTRY,
): OwnedWorkspaceTool[] {
	return moduleWorkspaceTools(registry)
		.filter((owned) => owned.tool.steps[step] !== undefined)
		.toSorted((a, b) => (a.tool.steps[step] ?? 0) - (b.tool.steps[step] ?? 0));
}

/**
 * Все плитки темы ОДНИМ списком, без разбивки по шагам.
 *
 * Панель «Инструменты» показывает именно этот список (2026-08-09). Набор,
 * меняющийся при переходе «Разобраться → Проверить», читался как пропажа
 * инструментов: человек видел «Аудиоформат» и «Интеллект-карту», переходил к
 * проверке — и они исчезали без всякого объяснения. Шаг остаётся смыслом
 * педагогики (порядок ниже это уважает), но не прячет инструменты.
 *
 * Порядок: сперва шаг, где плитка появляется раньше всего, затем её позиция
 * внутри этого шага — набор сохраняет привычную последовательность.
 */
export function moduleWorkspaceToolsAllSteps(
	registry: readonly WebModuleManifest[] = WEB_MODULE_REGISTRY,
): OwnedWorkspaceTool[] {
	const STEPS: readonly CourseToolStep[] = [1, 2, 3];
	const rank = (tool: WebWorkspaceTool): number => {
		for (const step of STEPS) {
			const order = tool.steps[step];
			if (order !== undefined) return step * 1000 + order;
		}
		return Number.MAX_SAFE_INTEGER;
	};
	return moduleWorkspaceTools(registry)
		.filter((owned) => STEPS.some((step) => owned.tool.steps[step] !== undefined))
		.toSorted((a, b) => rank(a.tool) - rank(b.tool));
}

/**
 * Порядок групп панели «Инструменты» — от самого спокойного способа взять тему
 * к самому деятельному, служебное планирование в конце.
 *
 * Порядок фиксирован здесь, а не в манифестах: это свойство ШКАЛЫ каналов, а не
 * отдельного модуля. Числа `order` внутри манифестов остаются про место плитки
 * в своей группе — их смысл не меняется.
 */
export const TOOL_CHANNEL_ORDER = [
	"read",
	"watch",
	"listen",
	"practice",
	"plan",
] as const satisfies readonly ToolChannel[];

/**
 * Цвет канала восприятия — по одной семантической категории UI-кита на канал.
 *
 * Живёт рядом с `TOOL_CHANNEL_ORDER` и ровно по той же причине: это свойство
 * ШКАЛЫ каналов, а не отдельного модуля и не отдельного экрана. Поверхностей,
 * которые красят плитки, уже три — панель инструментов темы, сцена «Добрать» и
 * вкладка «Инструменты» справки, — и разъехавшийся цвет означал бы, что человек
 * не узнаёт в справке тот же инструмент, что видел в курсе.
 *
 * Пары взяты так, чтобы цвет объяснял сам себя и чтобы соседние группы не
 * сливались: «читать» — приглушённый текстовый, «смотреть» — бирюзовый
 * (материалы-картинки), «слушать» — зелёный, «практиковаться» — синий,
 * «планировать» — нейтральный серый времени.
 *
 * Цвет — ОДИН на группу, а не по плитке: разноцветность внутри группы спорила бы
 * с её заголовком (поле `category` у `WebWorkspaceTool` убрано именно поэтому).
 */
export const TOOL_CHANNEL_CATEGORY = {
	read: "text",
	watch: "image",
	listen: "mastery",
	practice: "cards",
	plan: "time",
} as const satisfies Record<ToolChannel, Category>;

/** Плитки одного канала восприятия. Пустых групп на выходе не бывает. */
export interface WorkspaceToolChannelGroup {
	readonly channel: ToolChannel;
	readonly tools: readonly OwnedWorkspaceTool[];
}

/**
 * Разложить готовый список плиток по каналам восприятия.
 *
 * Вход — уже отфильтрованный список (доступность модулей считает поверхность),
 * поэтому функция чистая и ничего не знает ни о реестре, ни о killswitch'ах.
 * Порядок ВНУТРИ группы сохраняется тем, каким пришёл: педагогическая
 * последовательность реестра остаётся нетронутой.
 *
 * Группа без единой плитки не возвращается вовсе — заголовок над пустотой
 * читается как «здесь что-то сломалось», а не как «модуль выключен».
 */
export function groupWorkspaceToolsByChannel(
	tools: readonly OwnedWorkspaceTool[],
): WorkspaceToolChannelGroup[] {
	const byChannel = Map.groupBy(tools, (owned) => owned.tool.channel);
	return TOOL_CHANNEL_ORDER.map((channel) => ({
		channel,
		tools: byChannel.get(channel) ?? [],
	})).filter((group) => group.tools.length > 0);
}

/** Баннер-объяснение раздела вместе с модулем-владельцем. */
export interface OwnedSectionIntro {
	readonly moduleId: ModuleId;
	readonly manifest: WebModuleManifest;
	readonly intro: WebSectionIntro;
}

/** Все объявленные баннеры-объяснения — вход гейта переводов. */
export function moduleSectionIntros(
	registry: readonly WebModuleManifest[] = WEB_MODULE_REGISTRY,
): OwnedSectionIntro[] {
	const out: OwnedSectionIntro[] = [];
	for (const manifest of registry) {
		if (manifest.intro) out.push({ moduleId: manifest.id, manifest, intro: manifest.intro });
	}
	return out;
}

/**
 * Баннер одного раздела — `undefined`, если модуль его не объявил.
 *
 * Именно так поверхность и узнаёт про баннер: страница не хранит своего текста
 * и не знает, есть ли он вообще (инвариант плана §10.2).
 */
export function moduleSectionIntro(
	moduleId: ModuleId,
	registry: readonly WebModuleManifest[] = WEB_MODULE_REGISTRY,
): OwnedSectionIntro | undefined {
	return moduleSectionIntros(registry).find((owned) => owned.moduleId === moduleId);
}

/** Префиксы модулей, на которых приложение рисует overlay-каркас. */
export function moduleOverlayShellPrefixes(
	registry: readonly WebModuleManifest[] = WEB_MODULE_REGISTRY,
): string[] {
	return registry.flatMap((m) => [...(m.overlayShellPrefixes ?? [])]);
}

/** Префиксы модулей, где глобальная панель репетитора не монтируется. */
export function moduleTutorSuppressedPrefixes(
	registry: readonly WebModuleManifest[] = WEB_MODULE_REGISTRY,
): string[] {
	return registry.flatMap((m) => [...(m.tutorSuppressedPrefixes ?? [])]);
}
